import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Sparkles, X, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Bulk "Suggest all" runner — starts a background job on the server and
// shows a live progress bar (done/total · applied · needs review · failed).
const RANK = { low: 0, medium: 1, high: 2 };

export const TaBulkRunner = ({ unresolvedCount, pendingConfidences = [], onFinished, registerStart }) => {
  const [job, setJob] = useState(null);
  const [autoApply, setAutoApply] = useState(true);
  const [minConf, setMinConf] = useState('high');
  const [starting, setStarting] = useState(false);
  const [applyingAll, setApplyingAll] = useState(false);
  const timer = useRef(null);
  const wasRunning = useRef(false);

  const poll = async () => {
    try {
      const r = await axios.get(`${API}/api/data-ops/ta-audit/bulk-status`, { withCredentials: true });
      const j = r.data?.job || null;
      setJob(j);
      const running = j?.status === 'running';
      if (running) timer.current = setTimeout(poll, 3000);
      else if (wasRunning.current) { wasRunning.current = false; onFinished?.(); }
      if (running) wasRunning.current = true;
    } catch { /* noop */ }
  };

  useEffect(() => { poll(); return () => clearTimeout(timer.current); }, []); // eslint-disable-line

  const start = async (assetIds) => {
    setStarting(true);
    try {
      const r = await axios.post(`${API}/api/data-ops/ta-audit/bulk-run`,
        { asset_ids: assetIds || null, auto_apply: autoApply, min_confidence: minConf }, { withCredentials: true });
      toast.success(`Started — suggesting TAs for ${r.data.total} assets${autoApply ? `, auto-applying ${minConf}+ confidence picks` : ''}.`);
      wasRunning.current = true;
      setJob(r.data);
      clearTimeout(timer.current);
      timer.current = setTimeout(poll, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start bulk run');
    } finally { setStarting(false); }
  };

  useEffect(() => { registerStart?.(start); }, [autoApply, minConf]); // eslint-disable-line

  const cancel = async () => {
    await axios.post(`${API}/api/data-ops/ta-audit/bulk-cancel`, {}, { withCredentials: true });
    toast.info('Cancelling after the current batch…');
  };

  const applyAll = async () => {
    setApplyingAll(true);
    try {
      const r = await axios.post(`${API}/api/data-ops/ta-audit/apply-all`, { min_confidence: minConf }, { withCredentials: true });
      toast.success(`Applied ${r.data.applied} suggested TAs`);
      onFinished?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply all failed');
    } finally { setApplyingAll(false); }
  };

  const pendingProposals = pendingConfidences.filter((c) => (RANK[(c || 'low').toLowerCase()] ?? 0) >= (RANK[minConf] ?? 2)).length;
  const running = job?.status === 'running';
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4 space-y-3" data-testid="ta-bulk-runner">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="accent-violet-600" checked={autoApply} onChange={e => setAutoApply(e.target.checked)} disabled={running} data-testid="ta-bulk-auto-apply" />
            <span className="text-slate-800">Auto-apply picks with confidence ≥</span>
          </label>
          <select value={minConf} onChange={e => setMinConf(e.target.value)} disabled={running} className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="ta-bulk-min-confidence">
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low (everything)</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          {pendingProposals > 0 && !running && (
            <Button size="sm" variant="outline" onClick={applyAll} disabled={applyingAll} className="h-8 border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="ta-bulk-apply-all">
              {applyingAll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
              Apply {pendingProposals} pending suggestion{pendingProposals === 1 ? '' : 's'} ({minConf}+)
            </Button>
          )}
          {running ? (
            <Button size="sm" variant="outline" onClick={cancel} className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="ta-bulk-cancel">
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          ) : (
            <Button size="sm" onClick={() => start(null)} disabled={starting || unresolvedCount === 0} className="h-8 bg-violet-600 hover:bg-violet-700 text-white" data-testid="ta-bulk-start">
              {starting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Suggest all unresolved ({unresolvedCount})
            </Button>
          )}
        </div>
      </div>

      {job && (
        <div data-testid="ta-bulk-progress">
          <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
            <span className="flex items-center gap-1.5">
              {running ? <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-600" /> : job.status === 'error' ? <AlertCircle className="w-3.5 h-3.5 text-rose-600" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
              {running ? 'Suggesting…' : job.status === 'done' ? 'Finished' : job.status === 'cancelled' ? 'Cancelled' : 'Error'}
              {' · '}{job.done}/{job.total} assets
            </span>
            <span className="tabular-nums font-semibold text-slate-800" data-testid="ta-bulk-pct">{pct}%</span>
          </div>
          <div className="h-2.5 w-full rounded-full bg-white border border-violet-200 overflow-hidden">
            <div className="h-full bg-violet-600 transition-[width] duration-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 mt-1.5">
            <span><b className="text-emerald-700">{job.applied}</b> applied</span>
            <span><b className="text-amber-700">{job.needs_review}</b> need your review</span>
            <span><b className="text-slate-700">{job.suggested}</b> suggested</span>
            <span><b className="text-rose-700">{job.failed}</b> no suggestion (too little asset info)</span>
            {job.error && <span className="text-rose-700">{job.error}</span>}
          </div>
        </div>
      )}
    </div>
  );
};
